// WAAP Analytics → AI-WAF — NATIVE charts (no Superset iframe). The BE
// pre-normalizes Superset dashboard 40 ("waap.AI-WAF") into typed widgets
// (number / pie / table / timeseries), same pipeline as the other boards.
//   GET /analytics/ai-waf/charts?tz=…&range=…&domains=…
import { useQuery } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { waapKeys, waapPath, WAAP_AREAS } from './_shared';
import type { WaapChartsResponse } from './dashboard-charts';

/** Time-range options for AI-WAF — the dashboard's "time_range" TEMPLATE values
 *  (matched verbatim in the dataset SQL), distinct from the native-range boards. */
export const AIWAF_TIME_RANGES = [
  'Last Hour',
  'Last 24 Hours',
  'Last 7 Days',
  'Last 30 Days',
  'This Month',
  'Last Month',
] as const;
export type AiWafTimeRange = (typeof AIWAF_TIME_RANGES)[number];

/** Read the normalized AI-WAF widgets for a website's account. `domains` is the
 *  optional hostname filter (empty = all). Returns null when the board is not
 *  configured server-side (400/404/501). */
export function useWaapAiWafCharts(
  domainId: string | undefined,
  range = 'Last 24 Hours',
  domains: string[] = [],
) {
  const domainsKey = [...domains].sort().join(',');
  return useQuery({
    queryKey: [...waapKeys.aspect(domainId ?? '', WAAP_AREAS.aiWafCharts), range, domainsKey] as const,
    enabled: !!domainId,
    retry: false,
    staleTime: 60_000,
    queryFn: async (): Promise<WaapChartsResponse | null> => {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const params = new URLSearchParams({ tz, range });
        if (domainsKey) params.set('domains', domainsKey);
        const path = waapPath(domainId as string, WAAP_AREAS.aiWafCharts);
        const res = await serviceApiV3.get<WaapChartsResponse>(`${path}?${params.toString()}`);
        return res.data;
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 400 || status === 404 || status === 501) return null;
        throw e;
      }
    },
  });
}
